import { Link, useParams } from 'react-router-dom'
import { useExecution } from '@/core/hooks'
import { SchemaOutput } from '@/core/schema-renderer/SchemaOutput'
import {
  Callout,
  CopyButton,
  ErrorNotice,
  Loading,
  formatDuration,
} from '@/shared/components'

export function ExecutionDetailPage() {
  const { executionId = '' } = useParams()
  const query = useExecution(executionId)

  if (query.isLoading) return <div className="page"><Loading /></div>
  if (query.error) {
    return (
      <div className="page">
        <ErrorNotice error={query.error} onRetry={() => query.refetch()} />
      </div>
    )
  }

  const row = query.data!
  const failed = row.status === 'failed'

  return (
    <div className="page">
      <Link to="/executions" className="small">
        ← History
      </Link>

      <div className="page-header" style={{ marginTop: 14 }}>
        <div className="page-header-row">
          <div>
            <h1 className="mono">{row.action_id}</h1>
            <p className="small muted" style={{ margin: '5px 0 0' }}>
              {row.connector_id} ·{' '}
              {row.executed_at ? new Date(row.executed_at).toLocaleString() : '—'}
            </p>
          </div>
          <div className="row">
            <span className={`badge ${failed ? 'badge-danger' : 'badge-ok'}`}>
              <span className="dot" />
              {row.status_code ?? row.status}
            </span>
            <span className="small muted">{formatDuration(row.duration_ms)}</span>
            {row.pages > 1 && <span className="badge badge-accent">{row.pages} pages</span>}
          </div>
        </div>
      </div>

      {failed && row.error && (
        <Callout kind="danger" title={row.error.code}>
          <p>{row.error.message}</p>
          {row.error.details?.provider_message && (
            <p className="small mono">{String(row.error.details.provider_message)}</p>
          )}
        </Callout>
      )}

      {row.truncated && (
        <Callout kind="warn">
          <p className="small">
            This record was over the 4KB history limit, so the stored input or
            output was trimmed. The action itself returned the full response.
          </p>
        </Callout>
      )}

      <div className="stack" style={{ marginTop: 16, gap: 16 }}>
        <section className="card">
          <div className="row" style={{ marginBottom: 9 }}>
            <h3 style={{ flex: 1 }}>Input</h3>
            <CopyButton value={JSON.stringify(row.input, null, 2)} label="Copy" />
          </div>
          <pre className="json">{JSON.stringify(row.input, null, 2)}</pre>
        </section>

        <section className="card">
          <div className="row" style={{ marginBottom: 9 }}>
            <h3 style={{ flex: 1 }}>What was sent upstream</h3>
            <span className="small faint">credentials already redacted</span>
          </div>
          <pre className="json">{JSON.stringify(row.request_preview, null, 2)}</pre>
        </section>

        <section className="card">
          <h3 style={{ marginBottom: 9 }}>Output</h3>
          <SchemaOutput output={row.output} />
        </section>

        <section className="card card-pad-0">
          <div className="kv">
            <div>Execution id</div>
            <div className="row-tight">
              <span className="mono truncate">{row.id}</span>
              <CopyButton value={row.id} />
            </div>
            <div>Connection</div>
            <div>
              <Link to={`/connections/${row.connection_id}`} className="mono">
                {row.connection_id}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
